import type { Dictionary } from '../i18n/registry.js';

/**
 * Ошибка апи в едином формате `{ error: { code, message } }`
 * (02-ARCHITECTURE.md §6.3). Код нужен экранам, чтобы отличать
 * «неверный пароль» от «ссылка протухла», не разбирая текст.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Сеть недоступна: до сервера не дошли, а не сервер ответил ошибкой. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Коды, на которые пользователь может как-то ответить: подождать, освободить
 * место, подтвердить почту. Всё остальное — «что-то пошло не так»: показывать
 * внутренние подробности незачем.
 */
const KNOWN_CODES: Readonly<Record<string, (t: Dictionary) => string>> = {
  quota_exceeded: (t) => t.errors.quotaExceeded,
  rate_limited: (t) => t.errors.rateLimited,
  email_not_verified: (t) => t.errors.emailNotVerified,
  generation_queue_unavailable: (t) => t.errors.queueUnavailable,
  export_queue_unavailable: (t) => t.errors.queueUnavailable,
  book_published: (t) => t.errors.bookPublished,
  book_not_publishable: (t) => t.errors.bookNotPublishable,
  payment_required: (t) => t.monetization.payment.required,
  promo_code_invalid: (t) => t.monetization.payment.promoInvalid,
  plan_limit_exceeded: (t) => t.monetization.unavailable,
  plan_locked: (t) => t.monetization.lockedError,
  looks_not_ready: (t) => t.errors.looksNotReady,
  characters_not_approved: (t) => t.errors.charactersNotApproved,
  generation_in_progress: (t) => t.errors.generationInProgress,
  story_draft_not_ready: (t) => t.errors.storyDraftNotReady,
  not_found: (t) => t.errors.notFound,
  payload_too_large: (t) => t.errors.payloadTooLarge,
  unsupported_media_type: (t) => t.errors.unsupportedMediaType,
  audio_duration_invalid: (t) => t.errors.audioDurationInvalid,
  audio_unreadable: (t) => t.errors.audioUnreadable,
  photo_consent_required: (t) => t.errors.photoConsentRequired,
  publication_consent_required: (t) => t.errors.publicationConsentRequired,
  variant_not_selected: (t) => t.errors.variantNotSelected,
  style_locked: (t) => t.errors.styleLocked,
};

/**
 * Коды, под которыми сервер шлёт не одно сообщение, а несколько разных:
 * «Сначала примените сюжет к книге» и «У разворота нет текста для озвучки»
 * приходят под одним `conflict`; `entity_in_use` — про героя, вещь или
 * локацию; `forbidden` — и про неподошедший пароль, и про отказ по правам.
 * Свести их к одной строке — потерять именно ту подсказку, ради которой
 * ошибку и показывают, поэтому для них берётся текст сервера.
 *
 * Сервер присылает этот текст на языке читателя: по его `User.locale`, а если
 * бренд такого языка не знает — на языке бренда (`api-core/src/i18n`). Раньше
 * здесь оставался русский текст на английской странице; чинить это в UI было
 * нечем, потому что выбор нужной из дюжины фраз знает только сервер.
 *
 * Список закрытый и покрыт тестом: код, которого нет ни здесь, ни в
 * `KNOWN_CODES`, показывается как «что-то пошло не так» — и это заметнее
 * всего именно там, где сервер прислал полезную инструкцию.
 */
const CONTEXTUAL_CODES: ReadonlySet<string> = new Set([
  'conflict',
  'entity_in_use',
  'forbidden',
  'validation_error',
  'weak_password',
  'invalid_credentials',
  'registration_closed',
]);

/**
 * Текст сервера полезен для диагностики, но UI не показывает его напрямую:
 * пользовательское сообщение выбирается из активной локали по коду ошибки.
 */
export function userErrorMessage(error: unknown, t: Dictionary): string {
  if (error instanceof NetworkError) return t.common.networkError;
  if (error instanceof ApiError) {
    const known = KNOWN_CODES[error.code];
    if (known) return known(t);
    if (CONTEXTUAL_CODES.has(error.code) && error.message) return error.message;
  }
  return t.common.unexpectedError;
}
